/* eslint-disable react-hooks/exhaustive-deps */
import { People, Person, UserIconFilled } from '@/components/icons'
import { LockedRating } from '@/components/profile-limit'
import { LIMIT, OFFSET } from '@/constants/Common'
import { useDebounce } from '@/hooks/useDebounce'
import { useRatingLock } from '@/hooks/useRatingLock'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useSearchClubMembers, useSearchPlayerQuery } from '@/services'
import { Player } from '@/types'
import { Combobox, Transition } from '@headlessui/react'
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

type Props = {
  setOpenSearchPlayer: Dispatch<SetStateAction<boolean>>
  onSelect: (value: Player) => void
  isClubMatchNew?: boolean
  exclude?: number[]
}

export function SearchPlayer({ onSelect, setOpenSearchPlayer, isClubMatchNew, exclude }: Props) {
  const { clubId } = useParams()
  const { user } = useAppSelector(userSelector)
  const isRatingLocked = useRatingLock()

  const [selected, setSelected] = useState<Player | null>(null)
  const [query, setQuery] = useState('')
  const [offset, setOffset] = useState(OFFSET)
  // Player | ClubMember
  const [players, setPlayers] = useState<Map<number, any>>(new Map())

  const debouncedQuery = useDebounce<string>(query || '', 200)

  const { data: resultSearchPlayer } = useSearchPlayerQuery({
    exclude: exclude ? exclude : [],
    filter: {
      lat: user?.addresses?.[0]?.latitude || null,
      lng: user?.addresses?.[0]?.longitude || null,
    },
    includeStaff: true,
    includeUnclaimedPlayers: true,
    limit: LIMIT,
    offset: offset,
    query: debouncedQuery ? debouncedQuery : '*',
  })

  const { data: resultSearchClubMembers } = useSearchClubMembers({
    clubId: Number(clubId),
    exclude: exclude ? exclude : [],
    filter: {
      lat: user?.addresses?.[0]?.latitude || null,
      lng: user?.addresses?.[0]?.longitude || null,
    },
    includePendingPlayers: true,
    includeStaff: true,
    includeUnclaimedPlayers: true,
    limit: LIMIT,
    offset: offset,
    query: debouncedQuery ? debouncedQuery : '*',
  })

  const handleScroll = (event: any) => {
    const { target } = event
    const hasMore = resultSearchClubMembers
      ? resultSearchClubMembers.result.hasMore
      : resultSearchPlayer
        ? resultSearchPlayer.result.hasMore
        : false

    if (target.scrollHeight - target.scrollTop === target.clientHeight && hasMore) {
      setOffset((pre) => pre + LIMIT)
    }
  }

  useEffect(() => {
    if (!isClubMatchNew && resultSearchPlayer)
      setPlayers((old) => {
        let temp: Map<number, any>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resultSearchPlayer.result.hits.forEach((player) => {
          temp.set(player.id, player)
        })

        return temp
      })

    if (isClubMatchNew && resultSearchClubMembers)
      setPlayers((old) => {
        let temp: Map<number, any>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resultSearchClubMembers.result.hits.forEach((player) => {
          temp.set(player.id, player)
        })

        return temp
      })
  }, [resultSearchPlayer, resultSearchClubMembers])

  return (
    <Combobox
      value={selected}
      onChange={(value: Player) => {
        onSelect(value)
        setSelected(value)
      }}
    >
      <div className="relative">
        <Combobox.Input
          className={`w-full p-4 border text-base text-navy-900 font-semibold focus:outline-none py-6 ${
            !query && !players.size ? 'rounded-2xl' : 'rounded-t-2xl'
          }`}
          displayValue={(value: string) => value}
          onChange={(event) => {
            setQuery(event.target.value)
            setOffset(OFFSET)
          }}
          placeholder="Search by player name"
          autoFocus
        />
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options
            className={`absolute z-50 w-full overflow-auto rounded-b-2xl bg-white max-h-48 shadow-2xl ${
              players.size === 0 ? 'pb-0' : 'pb-6'
            }`}
            onScroll={(e: any) => handleScroll(e)}
          >
            {players.size === 0 && query !== '' ? (
              <div className="relative cursor-default select-none py-2 px-4 text-navy-900">Nothing found.</div>
            ) : (
              Array.from(players.values()).map((item: any) => (
                <Combobox.Option
                  key={item.id}
                  className={({ active }) =>
                    `relative cursor-default select-none ${
                      active ? 'bg-navy-900 bg-opacity-10 text-navy-900' : 'text-navy-900'
                    }`
                  }
                  value={item}
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpenSearchPlayer(false)
                  }}
                >
                  {() => (
                    <div className="flex justify-between p-4 cursor-pointer">
                      <div className="flex items-center font-semibold">
                        {item?.imageUrl ? (
                          <img src={item.imageUrl} className="h-10 w-10 rounded-full" alt="User avatar" />
                        ) : (
                          <UserIconFilled className="w-10 h-10 text-navy-900" />
                        )}
                        <div className="flex flex-col">
                          <span className="text-xs xs:text-sm ml-2">{item?.fullName}</span>
                          <span className="text-xs xs:text-sm ml-2">{item?.shortAddress}</span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="bg-gray-300 rounded-lg mr-1.5 xs:mr-2.5 flex flex-col justify-center items-center w-10 h-10 ">
                          <People className="text-navy-900" />
                          <span className="mt-1 text-navy-900 text-xs font-medium">
                            {isRatingLocked(item?.id) ? (
                              <LockedRating />
                            ) : isClubMatchNew ? (
                              item?.doubles
                            ) : (
                              item?.ratings?.doubles
                            )}
                          </span>
                        </div>
                        <div className="bg-gray-300 rounded-lg mr-1.5 xs:mr-2.5 flex flex-col justify-center items-center w-10 h-10 ">
                          <Person className="text-navy-900" />
                          <span className="mt-1 text-navy-900 text-xs font-medium">
                            {isRatingLocked(item?.id) ? (
                              <LockedRating />
                            ) : isClubMatchNew ? (
                              item?.singles
                            ) : (
                              item?.ratings?.singles
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </Combobox.Option>
              ))
            )}
            {debouncedQuery &&
              ((!clubId && resultSearchPlayer?.result?.hasMore) ||
                (clubId && resultSearchClubMembers?.result?.hasMore)) && (
                <div className="flex justify-center mt-2">
                  <span className="inline-flex items-center gap-px">
                    <span className="mx-2 h-1.5 w-1.5 animate-blink rounded-full bg-primary"></span>
                    <span className="mx-2 h-1.5 w-1.5 animate-blink rounded-full bg-primary delay-200"></span>
                    <span className="mx-2 h-1.5 w-1.5 animate-blink rounded-full bg-primary delay-[400ms]"></span>
                  </span>
                </div>
              )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  )
}
